import { useEffect, useRef, useState } from "react";
import {
    createQrPayment,
    getQrPaymentStatus,
} from "../../services/qrPaymentService";

const POLL_INTERVAL = 3000;
const TIMEOUT_MS = 10 * 60 * 1000;

export function useQrPayment({ onSuccess } = {}) {
    const [qr, setQr] = useState(null);
    const [status, setStatus] = useState("idle");
    // idle | creating | waiting | success | cancel | timeout | error

    const [error, setError] = useState(null);
    const [remainingMs, setRemainingMs] = useState(TIMEOUT_MS);

    const pollRef = useRef(null);
    const timeoutRef = useRef(null);
    const countdownRef = useRef(null);
    const retryRef = useRef(0);

    const clearAll = () => {
        clearInterval(pollRef.current);
        clearInterval(countdownRef.current);
        clearTimeout(timeoutRef.current);
    };

    const startCountdown = () => {
        const start = Date.now();

        countdownRef.current = setInterval(() => {
            const diff = TIMEOUT_MS - (Date.now() - start);
            setRemainingMs(Math.max(0, diff));
        }, 1000);
    };

    const startPolling = (refno) => {
        startCountdown();

        pollRef.current = setInterval(async () => {
            try {
                const res = await getQrPaymentStatus(refno);

                retryRef.current = 0; // reset retry เมื่อ success call
                const result = res?.data;

                if (result?.status_response?.data?.payment_status === "success") {
                    setStatus("success");
                    clearAll();
                    onSuccess?.(res);
                }

                if (res.status === "CANCEL") {
                    setStatus("cancel");
                    clearAll();
                }
            } catch (err) {
                retryRef.current += 1;

                // retry ได้ 3 ครั้ง
                if (retryRef.current >= 3) {
                    setError(err);
                    setStatus("error");
                    clearAll();
                }
            }
        }, POLL_INTERVAL);

        timeoutRef.current = setTimeout(() => {
            setStatus("timeout");
            clearAll();
        }, TIMEOUT_MS);
    };

    const createPayment = async (payload) => {
        try {
            setStatus("creating");

            const res = await createQrPayment(payload);
            // สมมติ API คืน { status: 200, data: {...} }
            if (res?.status !== 200) {
                throw new Error("create qr payment failed");
            }

            const qrData = res.data;

            setQr(qrData);
            setStatus("waiting");

            startPolling(qrData.refno);
        } catch (e) {
            setError(e);
            setStatus("error");
        }
    };

    useEffect(() => () => clearAll(), []);

    return {
        qr,
        status,
        error,
        remainingMs,
        createPayment,
    };
}
